import View from './view.js'
import { useEffect, useMemo, useRef } from 'react'
import { useDataChange, useDataValue } from 'Simple/Data'

export default function Logic(props) {
  let innerRef = useRef()
  let containerRef = useRef()

  let isCalendarSidebarFloating = useDataValue({
    context: 'tab',
    viewPath: props.viewPath,
    path: 'sidebar.is_floating',
  })
  let isCalendarSidebarFloatingAndVisible = useDataValue({
    context: 'tab',
    viewPath: props.viewPath,
    path: 'sidebar.is_floating_and_visible',
  })
  let isAgendaSidebarOpen = useDataValue({
    context: 'global',
    path: 'sidebar_toggles.is_agenda_sidebar_open',
    viewPath: props.viewPath,
  })
  let isChairsSidebarOpen = useDataValue({
    context: 'global',
    path: 'sidebar_toggles.is_chairs_sidebar_open',
    viewPath: props.viewPath,
  })
  let screenWidth = useDataValue({
    context: 'tab',
    viewPath: props.viewPath,
    path: 'screen_width',
  })
  let media = useDataValue({
    context: 'media',
    viewPath: props.viewPath,
  })
  let change = useDataChange({
    context: 'tab',
    viewPath: props.viewPath,
  })

  let sidebarTopSpace = useMemo(() => {
    if (innerRef.current) {
      return innerRef.current.offsetHeight
    } else return 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalendarSidebarFloatingAndVisible, isCalendarSidebarFloating])

  let sidebarHeight = useMemo(() => {
    return `calc(100vh - ${sidebarTopSpace}px)`
  }, [sidebarTopSpace])

  useEffect(() => {
    let handleResize = () => {
      change(next => {
        next.screen_width = window.innerWidth
      })
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    let agendaSidebar = document.getElementById('agenda-sidebar')
    let chairsSidebar = document.getElementById('chairs-sidebar')
    let calendarSidebar = document.getElementById('calendar-sidebar')

    let agendaWidth = agendaSidebar?.offsetWidth || 0
    let chairsWidth = chairsSidebar?.offsetWidth || 0
    let calendarWidth = calendarSidebar?.offsetWidth || 0

    let isCalendarSidebarDocked = !isCalendarSidebarFloating

    change(next => {
      let numberOfSidebarsVisible, sidebarWidth

      if (media.mobile) {
        // In a mobile device, chairs and agenda sidebars occupy the entire view,
        // so to calculate the timegrid width we only consider the calendar sidebar
        numberOfSidebarsVisible = isCalendarSidebarDocked ? 1 : 0
        sidebarWidth = isCalendarSidebarDocked ? calendarWidth : 0
      } else {
        // In bigger devices, chair, agenda and calendar (docked) sidebars can be
        // opened at the same time and that will change the space left for timegrid
        numberOfSidebarsVisible =
          (isAgendaSidebarOpen ? 1 : 0) +
          (isChairsSidebarOpen ? 1 : 0) +
          (isCalendarSidebarDocked ? 1 : 0)
        sidebarWidth =
          (isAgendaSidebarOpen ? agendaWidth : 0) +
          (isChairsSidebarOpen ? chairsWidth : 0) +
          (isCalendarSidebarDocked ? calendarWidth : 0)
      }

      next.timegrid_width = screenWidth - sidebarWidth
      // We use this is_full_width field to decide whether or not to show
      // a condensed version of the calendar topbar. If device is small
      // or we have open sidebars that do not allow enough space, we set
      // this to false and the condensed version will be displayed
      next.is_full_width =
        media.mobile || media.tablet
          ? false
          : // All three sidebars (calendar, agenda and chairs) are visible
            (screenWidth >= 1920 && numberOfSidebarsVisible === 3) ||
            // Any two of the sidebars are open
            (screenWidth >= 1680 && numberOfSidebarsVisible === 2) ||
            // One sidebar is open
            (screenWidth >= 1440 && numberOfSidebarsVisible === 1) ||
            // Neither of the sidebars are open
            (screenWidth >= 1280 && numberOfSidebarsVisible === 0)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAgendaSidebarOpen,
    isChairsSidebarOpen,
    isCalendarSidebarFloating,
    media.mobile,
    media.tablet,
    screenWidth,
  ])

  return (
    <View
      innerRef={innerRef}
      containerRef={containerRef}
      sidebarTopSpace={sidebarTopSpace}
      sidebarHeight={sidebarHeight}
      {...props}
    />
  )
}
